import { EditDatePicker } from "@/components/ui/EditDatePicker";
import { CircleAlert, Home, Info, MapPin, Trash, User } from "lucide-react";
import styles from "../../../styles/applicationDetail.module.css";
import editStyles from "../../../styles/editForm.module.css";
import * as angErr from "../../../utils/aboutYourselfAngularInlineMessages";
import { isCurrentAddressValidForPreviousSection } from "../../../utils/aboutYourselfValidator";
import {
  resolveCurrentAddressForPreviousRules,
  shouldShowPreviousAddressEditSection,
} from "../../../utils/previousAddressLogic";
import {
  COMMUNICATION_PREFERENCES,
  coalesceAddressLikeForRules,
  formatPhoneInput,
  formatSSNInput,
  parseSSNInput,
} from "../aboutYourselfTabHelpers";
import { AboutYourselfConfirmDialogs } from "./AboutYourselfConfirmDialogs";
import { AddressEditForm } from "./AddressEditForm";
import { AlternateAddressEditForm } from "./AlternateAddressEditForm";
import { inputErrorClass, withInputError } from "./aboutYourselfEditFieldHelpers";
import type { AboutYourselfEditViewProps } from "../../../types/tabs";
import type { PreviousAddressRow } from "../../../types/utils";
import { VerifastIdentitySection } from "../VerifastIdentitySection";
function AngularShowControlError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className={editStyles.showControlError} role="alert">
      <CircleAlert className={editStyles.showControlErrorIcon} aria-hidden />
      <span>{message}</span>
    </div>
  );
}

export function AboutYourselfEditView({
  view = "personal",
  applicationAllData,
  applicationData,
  applicantId,
  formData,
  currentAddress,
  currentAddressForm,
  previousAddressForms,
  alternateAddressForms,
  exposeInlineValidation,
  personalFieldsLocked = false,
  personalFieldsDirty,
  communicationPreferenceHelpOpen,
  setCommunicationPreferenceHelpOpen,
  sendingLink,
  noAlternateAddressUS,
  pendingVerifyEmail,
  pendingSendLink,
  onInputChange: handleInputChange,
  onSendApplicationLink: handleSendApplicationLink,
  onCurrentAddressChange: handleCurrentAddressChange,
  onPreviousAddressChange: handlePreviousAddressChange,
  onRemovePreviousAddress: handleRemovePreviousAddress,
  onAlternateAddressChange: handleAlternateAddressChange,
  onAddAlternateAddress: handleAddAlternateAddress,
  onRemoveAlternateAddress: handleRemoveAlternateAddress,
  onNoAlternateAddressChange: handleNoAlternateAddressChange,
  hasNonUSAddress,
  getAddressMoveInYmd,
  onVerify,
  onCancelVerify,
  onSendApplicationLinkConfirm,
  onCancelSendApplicationLink,
}: AboutYourselfEditViewProps) {
    const personalLocked = personalFieldsLocked === true;
    const currentAddrForPrev = resolveCurrentAddressForPreviousRules(
      currentAddressForm,
      currentAddress as PreviousAddressRow | undefined,
    );
    const currentAddrForRules = coalesceAddressLikeForRules(
      currentAddrForPrev,
      true,
    ) as PreviousAddressRow | null;
    const showPrev = shouldShowPreviousAddressEditSection(
      currentAddrForRules,
      previousAddressForms,
      isCurrentAddressValidForPreviousSection(currentAddrForPrev),
    );
    const showPersonalFieldMessage = (key: string) =>
      exposeInlineValidation || personalFieldsDirty[key];
    const personalFieldErr = (key: string, resolver: () => string | null) =>
      showPersonalFieldMessage(key) ? resolver() : null;

    const firstNameErr = personalFieldErr("first_name", () =>
      angErr.angularFirstNameError(formData.first_name),
    );
    const middleNameErr = personalFieldErr("middle_name", () =>
      angErr.angularMiddleNameError(formData.middle_name),
    );
    const lastNameErr = personalFieldErr("last_name", () =>
      angErr.angularLastNameError(formData.last_name),
    );
    const birthdateErr = personalFieldErr("birthdate", () =>
      angErr.angularBirthdateError(formData.birthdate),
    );
    const ssnErr = personalFieldErr("security_number", () =>
      angErr.angularSsnError(formData.security_number),
    );
    const mobileErr = personalFieldErr("mobile_no", () =>
      angErr.angularMobileError(formData.mobile_no),
    );
    const emailErr = personalFieldErr("email", () =>
      angErr.angularEmailError(formData.email),
    );
    const communicationPrefErr = personalFieldErr("communication_preference", () =>
      angErr.angularCommunicationPreferenceError(formData.communication_preference),
    );

    const isAddressView = view === "address";

    return (
      <>
      <div
        className={`${styles.tabContent} ${styles.aboutYourselfPremium} ${styles.aboutYourselfEditPremium}`}
        data-testid={isAddressView ? "application-address-tab-edit-page" : "application-about-tab-edit-page"}
      >
        {!isAddressView && (
        <>
        {/* Personal Information - Edit Mode */}
        <div className={styles.section} data-testid="application-about-tab-section-personal-profile">
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionTitleIcon} aria-hidden="true">
              <User className="h-4 w-4" />
            </span>
            Your Personal Profile Info *
          </h2>
          <div className={`${editStyles.formRow} ${styles.equalFourColumnRow}`}>
            <div className={editStyles.formGroup}>
              <label className={editStyles.label} data-testid="application-about-tab-first-name-label">
                First Name<span className={editStyles.required}>*</span>
              </label>
              <input
                type="text"
                className={withInputError(editStyles.input, Boolean(firstNameErr))}
                value={formData.first_name}
                data-testid="application-about-tab-first-name-input"
                onChange={(e) =>
                  handleInputChange("first_name", e.target.value)
                }
                placeholder="First Name"
                disabled={personalLocked}
                readOnly={personalLocked}
              />
              <AngularShowControlError message={firstNameErr} />
            </div>
            <div className={editStyles.formGroup}>
              <label className={editStyles.label} data-testid="application-about-tab-middle-name-label">Middle Name</label>
              <input
                type="text"
                className={withInputError(editStyles.input, Boolean(middleNameErr))}
                value={formData.middle_name}
                data-testid="application-about-tab-middle-name-input"
                onChange={(e) =>
                  handleInputChange("middle_name", e.target.value)
                }
                placeholder="Middle Name"
                disabled={personalLocked}
                readOnly={personalLocked}
              />
              <AngularShowControlError message={middleNameErr} />
            </div>
            <div className={editStyles.formGroup}>
              <label className={editStyles.label} data-testid="application-about-tab-last-name-label">
                Last Name<span className={editStyles.required}>*</span>
              </label>
              <input
                type="text"
                className={withInputError(editStyles.input, Boolean(lastNameErr))}
                value={formData.last_name}
                data-testid="application-about-tab-last-name-input"
                onChange={(e) => handleInputChange("last_name", e.target.value)}
                placeholder="Last Name"
                disabled={personalLocked}
                readOnly={personalLocked}
              />
              <AngularShowControlError message={lastNameErr} />
            </div>
            <div className={editStyles.formGroup}>
              <label className={editStyles.label} data-testid="application-about-tab-suffix-label">Suffix</label>
              <input
                type="text"
                className={editStyles.input}
                value={formData.suffix}
                data-testid="application-about-tab-suffix-input"
                onChange={(e) => handleInputChange("suffix", e.target.value)}
                placeholder="Suffix"
                disabled={personalLocked}
                readOnly={personalLocked}
              />
            </div>
          </div>

          <div
            className={`${editStyles.formRow} ${styles.communicationResendRow}`}
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-start",
              gap: "12px",
            }}
          >
            <div
              className={`${editStyles.formGroup} ${styles.communicationPreferenceGroup}`}
              style={{ flex: "0 0 auto", width: "auto", minWidth: 0 }}
            >
              <label className={editStyles.label} data-testid="application-about-tab-date-of-birth-label">
                Date of Birth<span className={editStyles.required}>*</span>
              </label>
              <div data-testid="application-about-tab-date-of-birth-input"><EditDatePicker
                value={formData.birthdate}
                onChange={(value) => handleInputChange("birthdate", value)}
                allowPastDates={true}
                allowFutureDates={false}
                displayFormat="mdy"
                className={inputErrorClass(Boolean(birthdateErr))}
                disabled={personalLocked}
              />
              <AngularShowControlError message={birthdateErr} />
              </div>
            </div>
            <div className={editStyles.formGroup}>
              <label className={editStyles.label} data-testid="application-about-tab-ssn-label">
                SSN or ITIN<span className={editStyles.required}>*</span>{" "}
                <span style={{ fontWeight: "normal", fontSize: "12px" }}>
                  (Do not include dashes)
                </span>
              </label>
              <input
                type="text"
                className={withInputError(editStyles.input, Boolean(ssnErr))}
                value={formatSSNInput(formData.security_number)}
                data-testid="application-about-tab-ssn-input"
                onChange={(e) =>
                  handleInputChange("security_number", parseSSNInput(e.target.value))
                }
                placeholder="XXX-XX-XXXX"
                maxLength={11}
                inputMode="numeric"
                autoComplete="off"
                disabled={personalLocked}
                readOnly={personalLocked}
              />
              <AngularShowControlError message={ssnErr} />
            </div>
            <div className={editStyles.formGroup}>
              <label className={editStyles.label} data-testid="application-about-tab-mobile-phone-label">
                Mobile Phone<span className={editStyles.required}>*</span>
              </label>
              <input
                type="tel"
                className={withInputError(editStyles.input, Boolean(mobileErr))}
                value={formatPhoneInput(formData.mobile_no)}
                data-testid="application-about-tab-mobile-phone-input"
                onChange={(e) => {
                  // Store only digits for the actual value
                  const digits = e.target.value.replace(/\D/g, "");
                  handleInputChange("mobile_no", digits);
                }}
                placeholder="(xxx) xxx-xxxx"
                disabled={personalLocked}
                readOnly={personalLocked}
              />
              <AngularShowControlError message={mobileErr} />
              <div className={editStyles.formGroupCheckbox}>
                <label className={editStyles.checkboxLabel} data-testid="application-about-tab-shared-phone-label">
                  <input
                    type="checkbox"
                    checked={formData.shared_phone_no}
                    data-testid="application-about-tab-shared-phone-checkbox"
                onChange={(e) =>
                      handleInputChange("shared_phone_no", e.target.checked)
                    }
                    disabled={personalLocked}
                  />
                  <span>This is a shared phone#</span>
                </label>
              </div>
            </div>
            <div className={editStyles.formGroup}>
              <label className={editStyles.label} data-testid="application-about-tab-email-label">
                Email<span className={editStyles.required}>*</span>
              </label>
              <input
                type="email"
                className={withInputError(editStyles.input, Boolean(emailErr))}
                value={formData.email}
                data-testid="application-about-tab-email-input"
                onChange={(e) => handleInputChange("email", e.target.value)}
                placeholder="Email"
                disabled={personalLocked}
                readOnly={personalLocked}
              />
              <AngularShowControlError message={emailErr} />
            </div>
          </div>

          <div className={`${editStyles.formRow} ${styles.communicationActionRow}`}>
            <div className={editStyles.formGroup}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  marginBottom: "0.3rem",
                  flexWrap: "wrap",
                }}
              >
                <label
                  className={editStyles.label}
                  style={{ marginBottom: 0 }}
                 data-testid="application-about-tab-communication-preference-label">
                  Communication Preference
                  <span className={editStyles.required}>*</span>
                </label>
                <div className={editStyles.communicationHelpWrap}>
                  <button
                    type="button"
                    className={editStyles.communicationHelpTrigger}
                    aria-label="About communication preference"
                    aria-expanded={communicationPreferenceHelpOpen}
                    data-testid="application-about-tab-communication-preference-help-button"
                onMouseEnter={() => setCommunicationPreferenceHelpOpen(true)}
                    onMouseLeave={() => setCommunicationPreferenceHelpOpen(false)}
                    onFocus={() => setCommunicationPreferenceHelpOpen(true)}
                    onBlur={() => setCommunicationPreferenceHelpOpen(false)}
                  >
                    <Info
                      className="h-[1.05rem] w-[1.05rem] text-green-600"
                      aria-hidden
                    />
                  </button>
                  {communicationPreferenceHelpOpen ? (
                    <div
                      className={editStyles.communicationHelpPopover}
                      role="tooltip"
                    >
                      <span>
                        Note: Email is our primary method currently.
                        <br />
                        We are shifting our messages from emails to text messages
                        for your convenience.
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>
              <div className={`${editStyles.radioGroup} ${styles.communicationPreferenceButtons}${communicationPrefErr ? ` ${editStyles.inputError}` : ""}`}>
                {COMMUNICATION_PREFERENCES.map((pref) => (
                  <label key={pref} className={editStyles.radioLabel} data-testid={`application-about-tab-communication-preference-${pref.toLowerCase().replace(/ /g, "-")}-label`}>
                    <input
                      type="radio"
                      name="communication_preference"
                      value={pref}
                      checked={formData.communication_preference === pref}
                      data-testid={`application-about-tab-communication-preference-${pref.toLowerCase().replace(/ /g, "-")}-input`}
                onChange={(e) =>
                        handleInputChange(
                          "communication_preference",
                          e.target.value,
                        )
                      }
                      disabled={personalLocked}
                    />
                    <span>{pref}</span>
                  </label>
                ))}
              </div>
              <AngularShowControlError message={communicationPrefErr} />
            </div>
            <div className={`${editStyles.formGroup} ${styles.resendApplicantLinkGroup}`}>
              <label className={editStyles.label} data-testid="application-about-tab-resend-applicant-link-label">Resend Applicant Link</label>
              <div className={editStyles.buttonGroup}>
                <button
                  type="button"
                  className={editStyles.btnSmall}
                  data-testid="application-about-tab-resend-applicant-link-email-button"
                  onClick={() => handleSendApplicationLink("email")}
                  disabled={sendingLink !== null}
                >
                  {sendingLink === "email" ? "Sending..." : "Email"}
                </button>
                <button
                  type="button"
                  className={editStyles.btnSmall}
                  data-testid="application-about-tab-resend-applicant-link-sms-button"
                  onClick={() => handleSendApplicationLink("sms")}
                  disabled={sendingLink !== null}
                >
                  {sendingLink === "sms" ? "Sending..." : "Mobile"}
                </button>
              </div>
            </div>
          </div>
        </div>

        <VerifastIdentitySection
          applicationAllData={applicationAllData}
          applicationData={applicationData}
          applicantId={applicantId}
          testIdPrefix="application-about-tab-edit"
        />

        </>
        )}

        {isAddressView && (
        <>
        {/* Current Address - Edit Mode */}
        <div className={styles.section} data-testid="application-about-tab-edit-section-current-address">
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionTitleIcon} aria-hidden="true">
              <Home className="h-4 w-4" />
            </span>
            Current Address - * (We require 2 years of addresses where
            you lived)
          </h2>
          <AddressEditForm
            addressData={currentAddressForm ?? currentAddress ?? null}
            addressType="current_address"
            addressIndex={0}
            onAddressChange={handleCurrentAddressChange}
            exposeInlineValidation={exposeInlineValidation}
          />
        </div>

        {/* Previous Addresses - Edit Mode (under 24 months at current, same as Angular) */}
        {showPrev && (
          <div className={`${styles.section} ${styles.aboutNestedSection}`} data-testid="application-about-tab-section-previous-address">
            <h2 className={`${styles.sectionTitle} ${styles.aboutSubsectionTitle}`}>
              <span className={styles.sectionTitleIcon} aria-hidden="true">
                <MapPin className="h-4 w-4" />
              </span>
              Previous Address<span className={editStyles.required}>*</span>
            </h2>
            {previousAddressForms.map((address, index) => (
              <div key={address?.ra_id || `prev-${index}`}>
                {!address?.ra_id && (
                  <p className={editStyles.helperText} style={{ marginBottom: "12px" }}>
                    You have not provided 24 months of address history. Please add another.
                  </p>
                )}
                <div className={styles.aboutAddressEditCard}>
                  <div className={styles.aboutAddressEditCardHeader}>
                    <h3>
                      Previous Address {index + 1}
                    </h3>
                    {index !== 0 && (
                      <button
                        type="button"
                        className={styles.aboutRemoveButton}
                        data-testid={`application-about-tab-previous-address-${index}-remove-button`}
                        onClick={() => handleRemovePreviousAddress(index)}
                      >
                        <Trash className="h-3.5 w-3.5" aria-hidden />
                        Remove
                      </button>
                    )}
                  </div>

                <AddressEditForm
                  addressData={address}
                  addressType="previous_address"
                  addressIndex={index}
                  previousAddressEndMoveInDate={
                    index === 0
                      ? getAddressMoveInYmd(
                          (currentAddressForm ?? currentAddress) as PreviousAddressRow,
                        )
                      : getAddressMoveInYmd(previousAddressForms[index - 1])
                  }
                  onAddressChange={(data) =>
                    handlePreviousAddressChange(index, data)
                  }
                  exposeInlineValidation={exposeInlineValidation}
                />
                </div>
              </div>
            ))}
            {/* Angular parity: no manual "Add Previous Address" button. Additional rows are
                appended automatically by `validateAddress()`-style effect when combined
                history is still under 24 months. */}
          </div>
        )}

        {/* Alternate Address for Credit Search (Optional) - Edit Mode */}
        <div className={`${styles.section} ${styles.aboutNestedSection}`} data-testid="application-about-tab-section-other-address-for-credit-search-optional">
          <h2 className={`${styles.sectionTitle} ${styles.aboutSubsectionTitle}`}>
            <span className={styles.sectionTitleIcon} aria-hidden="true">
              <MapPin className="h-4 w-4" />
            </span>
            Other Address for Credit Search (Optional)
          </h2>
          <p className={editStyles.helperText}>
            If for some reason, we are unable to match your address(es) above to
            your credit file, you may enter another address here.
          </p>

          {/* "I don't have an alternate address in the US" — Angular `*ngIf="nonUSAddress"` */}
          {hasNonUSAddress() && (
            <div
              className={editStyles.formRow}
              style={{ marginTop: "12px", marginBottom: "12px" }}
            >
              <div className={editStyles.formGroupCheckbox}>
                <label className={editStyles.checkboxLabel} data-testid="application-about-tab-no-alternate-address-label">
                  <input
                    type="checkbox"
                    checked={noAlternateAddressUS}
                    data-testid="application-about-tab-no-alternate-address-checkbox"
                onChange={(e) =>
                      handleNoAlternateAddressChange(e.target.checked)
                    }
                  />
                  <span>I don't have an alternate address in the US</span>
                </label>
              </div>
            </div>
          )}

          {/* Existing Alternate Addresses */}
          {alternateAddressForms.map((address, index) => (
            <div
              key={index}
              className={styles.aboutAddressEditCard}
            >
              <div
                className={styles.aboutAddressEditCardHeader}
              >
                <h3>
                  Alternate Address {index + 1}
                </h3>
                <button
                  type="button"
                  className={styles.aboutRemoveButton}
                  data-testid={`application-about-tab-alternate-address-${index}-remove-button`}
                onClick={() => handleRemoveAlternateAddress(index)}
                >
                  <Trash className="h-3.5 w-3.5" aria-hidden />
                  Remove
                </button>
              </div>
              <AlternateAddressEditForm
                addressData={address}
                alternateIndex={index}
                exposeInlineValidation={exposeInlineValidation}
                onChange={(data) =>
                  handleAlternateAddressChange(index, data)
                }
              />
            </div>
          ))}

          {/* Add Other Address Button */}
          <div style={{ marginTop: "16px" }}>
            <button
              type="button"
              className={styles.btnSmall}
              data-testid="application-about-tab-add-alternate-address-button"
                onClick={handleAddAlternateAddress}
              >
              Add Other Address
            </button>
          </div>
        </div>
        </>
        )}
      </div>

      <AboutYourselfConfirmDialogs
        pendingVerifyEmail={pendingVerifyEmail}
        pendingSendLink={pendingSendLink}
        onVerify={onVerify}
        onCancelVerify={onCancelVerify}
        onSendApplicationLink={onSendApplicationLinkConfirm}
        onCancelSendApplicationLink={onCancelSendApplicationLink}
        testIdPrefix="application-about-tab-edit-confirm"
      />
    </>
    );
}
